import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { AxiosError } from "axios";
import HttpService from "../services/http";

export interface PasswordRule {
  regex: string;
  description: string;
}

/** The levels whose requirement has a translation (`passwordLevels.<level>`). */
export const KNOWN_PASSWORD_LEVELS = new Set([
  "level1",
  "level2",
  "level3",
  "level4",
  "level5",
  "level6",
]);

export interface PasswordLevelRules {
  level: string;
  rules: PasswordRule[];
}

export interface PasswordRulesPayload extends PasswordLevelRules {
  /** The required level, then each stronger one, as UMD accepts them. */
  accepted?: PasswordLevelRules[];
}

/** As UMD's passwordIsAcceptable: the password meets every rule of the required level, or of a stronger one. */
export const meetsPasswordRules = (
  password: string,
  required: PasswordLevelRules,
  accepted?: PasswordLevelRules[]
) =>
  (accepted?.length ? accepted : [required]).some(({ rules }) =>
    rules.every((rule) => new RegExp(rule.regex).test(password))
  );

interface PublicEntityInfo {
  entity_informations?: {
    entity_name?: string;
    entity_logo?: string | null;
    entity_description?: string | null;
  };
}

type UsePasswordResetContextOptions = {
  redirectOnError?: string;
};

export const usePasswordResetContext = (
  userIdentification: string | null,
  entityIdentification: string | null,
  token: string | null,
  { redirectOnError }: UsePasswordResetContextOptions = {}
) => {
  const navigate = useNavigate();

  const enabled = !!userIdentification && !!entityIdentification && !!token;

  const entityQuery = useQuery<PublicEntityInfo, AxiosError<any, any>>(
    ["public-entity-info", entityIdentification],
    async () => {
      const res = await HttpService.get(
        `/link-registration/${entityIdentification}/service-basic-info`
      );
      return res?.data?.element as PublicEntityInfo;
    },
    { enabled, retry: false }
  );

  const passwordQuery = useQuery<PasswordRulesPayload, AxiosError<any, any>>(
    ["password-rules", entityIdentification],
    async () => {
      const res = await HttpService.get("/public/password-rules", {
        params: { entity_identification: entityIdentification },
      });
      return res?.data?.element as PasswordRulesPayload;
    },
    { enabled, retry: false }
  );

  const isError = entityQuery.isError || passwordQuery.isError;

  useEffect(() => {
    if (isError && redirectOnError) {
      navigate(redirectOnError, { replace: true });
    }
  }, [isError, redirectOnError, navigate]);

  return {
    entityLogo: entityQuery.data?.entity_informations?.entity_logo ?? null,
    entityName: entityQuery.data?.entity_informations?.entity_name ?? null,
    passwordLevel: passwordQuery.data?.level ?? null,
    passwordRules: passwordQuery.data?.rules ?? [],
    acceptedLevels: passwordQuery.data?.accepted,
    isLoading: entityQuery.isLoading || passwordQuery.isLoading,
    isError,
  };
};

export const usePasswordRules = (entityIdentification: string | null) => {
  const enabled = !!entityIdentification;

  const query = useQuery<PasswordRulesPayload, AxiosError<any, any>>(
    ["password-rules", entityIdentification],
    async () => {
      const res = await HttpService.get("/public/password-rules", {
        params: { entity_identification: entityIdentification },
      });
      return res?.data?.element as PasswordRulesPayload;
    },
    { enabled, retry: false }
  );

  return {
    passwordLevel: query.data?.level ?? null,
    passwordRules: query.data?.rules ?? [],
    acceptedLevels: query.data?.accepted,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
